import React from "react";
import toast from "react-hot-toast";
import { FaMagnifyingGlass, FaLightbulb } from "react-icons/fa6";
import { API, hasUsableWebsite, worthAssessment } from "../appShared";

export default function InfoTab({ lead, updateLead, findEmail, crawlEmail, crawlProjectDetails, setNote, note, saveNote, searching }) {
  const openExternal = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const [projectCrawling, setProjectCrawling] = React.useState(false);
  const [guesses, setGuesses] = React.useState([]);
  const [pinging, setPinging] = React.useState({});
  const [guessesShown, setGuessesShown] = React.useState(lead.websiteStatus === "missing");

  React.useEffect(() => {
    if (lead.websiteStatus === "missing") {
      const raw = (lead.name || "").toLowerCase()
        .replace(/\s+(as|da|ab|inc|llc|ltd|gmbh|sa|oy|oslo|bergen|stavanger|norway|norge)\b/gi, "")
        .replace(/[^a-z0-9æøå]/g, "")
        .replace(/[æ]/g, "a").replace(/[ø]/g, "o").replace(/[å]/g, "a");
      const tlds = [".no", ".com", ".org", ".net"];
      setGuesses(tlds.map((tld) => raw + tld));
    }
  }, [lead.id, lead.name, lead.website, lead.websiteStatus]);

  const pingAndSave = async (domain) => {
    setPinging((prev) => ({ ...prev, [domain]: "checking" }));
    try {
      const response = await fetch(`${API}/api/proxy/ping-website?url=${encodeURIComponent(domain)}`);
      const data = await response.json();
      if (data.ok) {
        setPinging((prev) => ({ ...prev, [domain]: "live" }));
        updateLead(lead.id, { website: "https://" + domain.replace(/^https?:\/\//, "") });
        toast.success(`✅ ${domain} is live — saved!`);
      } else {
        setPinging((prev) => ({ ...prev, [domain]: "dead" }));
      }
    } catch {
      setPinging((prev) => ({ ...prev, [domain]: "dead" }));
    }
  };

  const copyLeadSummary = async () => {
    const summary = [
      `Company: ${lead.name || "-"}`,
      `Industry: ${lead.industry || "-"}`,
      `Location: ${[lead.city, lead.country].filter(Boolean).join(", ") || "-"}`,
      `Address: ${lead.address || "-"}`,
      `Website: ${lead.website || "No website"}`,
      `Phone: ${lead.phone || "-"}`,
      `Email: ${lead.email || "-"}`,
      `Status: ${lead.status || "New"}`,
      `Score: ${lead.score || 0}/100`,
      `Source: ${lead.source || lead.country || "-"}`,
      `Org Number: ${lead.orgNumber || "-"}`,
      `Added: ${lead.addedAt || "today"}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(summary);
      toast.success("Lead summary copied");
    } catch {
      toast.error("Could not copy summary");
    }
  };

  const searchTerm = encodeURIComponent([lead.name, lead.city, lead.country].filter(Boolean).join(" "));
  const mapTerm = encodeURIComponent(lead.address || [lead.name, lead.city, lead.country].filter(Boolean).join(" "));
  const projectServices = Array.isArray(lead.projectServices) ? lead.projectServices : [];
  const projectSignals = Array.isArray(lead.projectSignals) ? lead.projectSignals : [];
  const worth = worthAssessment(lead);

  const handleProjectCrawl = async () => {
    setProjectCrawling(true);
    await crawlProjectDetails(lead);
    setProjectCrawling(false);
  };

  return (
    <div className="info-tab" style={{ pointerEvents: searching ? "none" : "auto" }}>
      {lead.websiteStatus === "missing" && (
        <div className="website-guesser-card" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="wg-header" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <span className="wg-icon">🌐</span>
            <div style={{ pointerEvents: searching ? "none" : "auto" }}>
              <div className="wg-title" style={{ pointerEvents: searching ? "none" : "auto" }}>Website not in Google Maps data</div>
              <div className="wg-sub" style={{ pointerEvents: searching ? "none" : "auto" }}>We generated likely domains — click to ping and save if live.</div>
            </div>
            <button className="wg-toggle" onClick={() => setGuessesShown((open) => !open)}>
              {guessesShown ? "▲" : "▼"}
            </button>
          </div>
          {guessesShown && (
            <div className="wg-chips" style={{ pointerEvents: searching ? "none" : "auto" }}>
              {guesses.map((domain) => {
                const state = pinging[domain];
                return (
                  <button
                    key={domain}
                    className={`wg-chip ${state === "live" ? "wg-live" : state === "dead" ? "wg-dead" : state === "checking" ? "wg-checking" : ""}`}
                    onClick={() => !state && pingAndSave(domain)}
                    disabled={!!state}
                  >
                    {state === "checking" ? "⏳" : state === "live" ? "✅" : state === "dead" ? "✗" : "🔍"} {domain}
                  </button>
                );
              })}
              <button className="wg-chip wg-google" onClick={() => openExternal(`https://www.google.com/search?q=${encodeURIComponent(lead.name + " " + (lead.city || "") + " official website")}`)}>
                🔎 Search Google
              </button>
            </div>
          )}
        </div>
      )}
      {lead.aiSummary && <div className="alert alert-ai" style={{ pointerEvents: searching ? "none" : "auto" }}>🤖 {lead.aiSummary}</div>}

      <div className="info-grid" style={{ pointerEvents: searching ? "none" : "auto" }}>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Source</span><span className="info-val">{lead.source || lead.country || "—"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Source Type</span><span className="info-val">{lead.sourceType || "—"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Status</span><span className="info-val">{lead.status || "New"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Industry</span><span className="info-val" data-tooltip-id="tt" data-tooltip-content={lead.industry || "—"}>{lead.industry || "—"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Team Size</span><span className="info-val">{lead.employees ? `~${lead.employees}` : "Unknown"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Website</span><span className="info-val">{hasUsableWebsite(lead) ? <a href={lead.website} target="_blank" rel="noreferrer">{lead.website}</a> : (lead.websiteStatus === "missing" ? "No website" : "Website unknown")}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Phone</span><span className="info-val">{lead.phone || "—"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Email</span><span className="info-val">{lead.email || "—"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Address</span><span className="info-val">{lead.address || "—"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Decision</span><span className="info-val">{lead.decision || "Undecided"}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Worth</span><span className="info-val" data-tooltip-id="tt" data-tooltip-content={worth.reason}>{worth.label}</span></div>
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Project Value</span><span className="info-val">{lead.projectValue || "Not set"}</span></div>
        {lead.sourceUrl && <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Source URL</span><span className="info-val"><a href={lead.sourceUrl} target="_blank" rel="noreferrer">Open source</a></span></div>}
        <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Legal Basis</span><span className="info-val">{lead.legalBasis || "—"}</span></div>
        {lead.postedAt && <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Posted At</span><span className="info-val">{lead.postedAt}</span></div>}
        {lead.linkedin && <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">LinkedIn</span><span className="info-val"><a href={lead.linkedin} target="_blank" rel="noreferrer">Open profile</a></span></div>}
        {lead.orgNumber && <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Org #</span><span className="info-val">{lead.orgNumber}</span></div>}
        {lead.rating && <div className="info-item" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Rating</span><span className="info-val">⭐ {lead.rating} ({lead.ratingCount || 0})</span></div>}
        <div className="info-item full-width" style={{ pointerEvents: searching ? "none" : "auto" }}><span className="info-key">Added</span><span className="info-val">{lead.addedAt || "today"}</span></div>
      </div>

      <div className="info-actions" style={{ pointerEvents: searching ? "none" : "auto" }}>
        <button className="btn-ghost btn-sm" onClick={copyLeadSummary}>Copy summary</button>
        <button className="btn-ghost btn-sm" onClick={() => openExternal(`https://www.google.com/search?q=${searchTerm}`)}>Google search</button>
        <button className="btn-ghost btn-sm" onClick={() => openExternal(`https://www.google.com/maps/search/?api=1&query=${mapTerm}`)}>Open maps</button>
        {lead.website && <button className="btn-ghost btn-sm" onClick={handleProjectCrawl} disabled={projectCrawling}>{projectCrawling ? "Crawling details..." : "Crawl project details"}</button>}
        {lead.websiteStatus === "missing" && <button className="btn-ghost btn-sm" onClick={() => openExternal(`https://www.google.com/search?q=${searchTerm}+official+website`)}>Find website</button>}
        <button className="btn-ghost btn-sm" onClick={() => updateLead(lead.id, { website: null, websiteStatus: "missing" })}>Confirm no website</button>
        <button className="btn-ghost btn-sm" onClick={() => updateLead(lead.id, { websiteStatus: "unknown" })}>Mark site unknown</button>
      </div>

      {lead.projectSummary && (
        <div className="project-crawl-card" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="project-crawl-title" style={{ pointerEvents: searching ? "none" : "auto" }}>Real project details from website crawl</div>
          <div className="project-crawl-summary" style={{ pointerEvents: searching ? "none" : "auto" }}>{lead.projectSummary}</div>

          {projectServices.length > 0 && (
            <div className="project-crawl-group" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <div className="section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Detected Services</div>
              <div className="project-chip-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
                {projectServices.map((service) => <span key={service} className="project-chip">{service}</span>)}
              </div>
            </div>
          )}

          {projectSignals.length > 0 && (
            <div className="project-crawl-group" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <div className="section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Project Signals</div>
              <div className="project-chip-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
                {projectSignals.map((signal) => <span key={signal} className="project-chip project-chip-signal">{signal}</span>)}
              </div>
            </div>
          )}

          <div className="project-crawl-meta" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <span>Pages crawled: {lead.projectPagesCrawled || 0}</span>
            {lead.projectContactEmails?.[0] && <span>Best email: {lead.projectContactEmails[0]}</span>}
          </div>
        </div>
      )}

      {lead.aiReasons?.length > 0 && (
        <div className="ai-reasons" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>AI Score Reasons</div>
          {lead.aiReasons.map((reason, index) => <div key={index} className="reason-item">• {reason}</div>)}
        </div>
      )}

      {!lead.email ? (
        <div className="hunter-block" style={{ pointerEvents: searching ? "none" : "auto" }}>
          {lead.website ? (
            <>
              <div className="hunter-title" style={{ pointerEvents: searching ? "none" : "auto" }}><FaMagnifyingGlass /> Find Decision Maker Email</div>
              <button className="btn-hunter" onClick={() => findEmail(lead)} data-tooltip-id="tt" data-tooltip-content="Search Hunter.io for contact emails (50 free/month)">
                Find Email via Hunter.io
              </button>
            </>
          ) : (
            <div className="alert alert-info" style={{ pointerEvents: searching ? "none" : "auto" }}><FaLightbulb style={{ marginRight: 4, color: "#f59e0b" }} />No verified website — Hunter.io needs a domain to search. Try finding their site or LinkedIn first.</div>
          )}
        </div>
      ) : (
        <div className="hunter-found" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <span className="hunter-found-label">📧 Email</span>
          <a href={`mailto:${lead.email}`} className="hunter-found-email">{lead.email}</a>
        </div>
      )}

      <div className="note-block" style={{ pointerEvents: searching ? "none" : "auto" }}>
        <textarea className="note-area" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a note about this lead…" rows={3} />
        <button className="btn-ghost btn-sm" onClick={saveNote}>Save note</button>
      </div>
    </div>
  );
}